import {
  Button,
  Flex,
  Loader,
  ScrollView,
  SearchField,
} from "@aws-amplify/ui-react";
import { API, graphqlOperation, Storage } from "aws-amplify";
import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import LocalizedStrings from "react-localization";
import { CModal, CModalBody, CModalHeader, CModalTitle } from "@coreui/react";

import { toolDocumentsByToolID } from "graphql/queries";
import { deleteToolDocument } from "graphql/mutations";
import usePermissions from "hooks/usePermission";
import ConfirmModal from "components/utils/ConfirmModal";
import ToolParamatersTable from "components/tools/documents/ToolDocumentsTable";
import ToolDocumentCreateForm from "./ToolDocumentCreateForm";
import ToolDocumentUpdateForm from "./ToolDocumentUpdateForm";

const ToolDocuments = ({ toolID, toolName, visible, onClose }) => {
  const [canCreate, canUpdate, canDelete] = usePermissions([
    "tool:create-doc",
    "tool:update-doc",
    "tool:delete-doc",
  ]);
  const [visibleState, setVisibleState] = useState(visible);
  const [toolDocuments, setToolDocuments] = useState([]);
  const [toolDocumentsLoading, setToolDocumentsLoading] = useState(false);
  const [paginationTokens, setPaginationTokens] = useState({
    previous: [],
    nextToken: null,
  });
  const [modalDetails, setModalDetails] = useState({
    visible: false,
    toolDocumentId: null,
    toolDocumentName: null,
  });
  const [modalToolDocument, setModalToolDocument] = useState({
    visible: false,
    toolDocumentId: null,
  });

  const columnsMapped = useMemo(
    () =>
      toolDocuments.map((item) => {
        return {
          ...item,
          actions: (
            <>
              {canUpdate && (
                <Button
                  className="px-1"
                  children={strings.actions.edit}
                  variation="link"
                  size="small"
                  onClick={() => openModal("update", item.id)}
                />
              )}
              <Button
                className="px-1"
                children={strings.actions.download}
                variation="link"
                size="small"
                onClick={() => downloadDoc(item.fileSrc)}
              />
              {canDelete && (
                <Button
                  className="px-1"
                  children={strings.actions.delete}
                  variation="link"
                  size="small"
                  onClick={() => {
                    setModalDetails({
                      visible: true,
                      toolDocumentId: item.id,
                      toolDocumentName: item.name,
                    });
                  }}
                />
              )}
            </>
          ),
        };
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [toolDocuments]
  );

  const onNext = () => {
    fetchToolDocuments(paginationTokens.nextToken);
  };

  const onPrevious = () => {
    const previous =
      paginationTokens.previous?.length <= 1
        ? null
        : paginationTokens.previous?.slice(-2)[0];
    fetchToolDocuments(previous, false);
  };

  const onKeywordSubmit = (keyword) => {
    const keywordSplit = keyword && keyword.split(" ");
    fetchToolDocuments(undefined, undefined, keywordSplit);
  };

  const openModal = (action, id) => {
    setVisibleState(false);
    setModalToolDocument({
      visible: true,
      action,
      toolDocumentId: id,
    });
  };

  const closeModal = () => {
    setVisibleState(true);
    setModalToolDocument({
      visible: false,
    });
  };

  const onSuccessAction = () => {
    closeModal();
    fetchToolDocuments();
  };

  const downloadDoc = async (src) => {
    try {
      const res = await Storage.get(`tools/${src}`, {
        level: "public",
      });
      const link = document.createElement("a");
      link.href = res;
      link.setAttribute("download", "");
      link.setAttribute("target", "_blank");
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.log("Error Download Document", err);
      toast.error(strings.responses.errorDownload);
    }
  };

  async function fetchToolDocuments(token, isNext = true, keywords) {
    try {
      setToolDocumentsLoading(true);
      const limitMapped = 10;

      const toolDocumentsData = await API.graphql(
        graphqlOperation(toolDocumentsByToolID, {
          toolID,
          limit: !keywords ? limitMapped : undefined,
          nextToken: token,
          filter: keywords
            ? {
                and: keywords.map((key) => ({
                  or: [
                    {
                      fileName: { contains: key },
                    },
                  ],
                })),
              }
            : {},
        })
      );
      let toolDocumentsItems =
        toolDocumentsData.data.toolDocumentsByToolID.items;

      setToolDocuments(toolDocumentsItems);
      setPaginationTokens((prevSt) => {
        let previous = prevSt.previous ?? [];
        if (isNext) previous = token ? [...previous, token] : [...previous];
        if (!isNext && previous?.length) previous.pop();

        return {
          previous,
          nextToken: toolDocumentsData.data.toolDocumentsByToolID.nextToken,
        };
      });
      setToolDocumentsLoading(false);
    } catch (err) {
      toast.error(strings.responses.errorFetch);
      setToolDocumentsLoading(false);
    } finally {
      setToolDocumentsLoading(false);
    }
  }

  async function toolDocumentDelete(id) {
    try {
      const result = await API.graphql(
        graphqlOperation(deleteToolDocument, { input: { id } })
      );
      toast.success(strings.successDeleted);
      const { id: deletedId, fileSrc: deletedSrc } =
        result.data.deleteToolDocument;
      setToolDocuments(toolDocuments.filter((item) => item.id !== deletedId));
      setModalDetails({
        visible: false,
        toolDocumentId: null,
      });

      if (deletedSrc)
        await Storage.remove(`tools/${deletedSrc}`, {
          level: "public",
        });
    } catch (err) {
      toast.error(strings.responses[err]);
    }
  }

  useEffect(() => {
    fetchToolDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setVisibleState(visible);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  return (
    <>
      <CModal
        size="xl"
        alignment="center"
        visible={visibleState}
        onClose={() => !modalToolDocument.visible && onClose()}
        backdrop="static"
      >
        <CModalHeader>
          <CModalTitle>
            {strings.title} - {toolName}
          </CModalTitle>
        </CModalHeader>
        <CModalBody style={{ overflow: "hidden" }}>
          <ScrollView maxHeight="75vh">
            <div>
              <div className="p-3">
                <Flex className="mb-1" justifyContent="space-between">
                  <div style={styles.searchByKeyword}>
                    <SearchField
                      label={strings.table.search.label}
                      placeholder={strings.table.search.placeholder}
                      onSubmit={onKeywordSubmit}
                      onClear={onKeywordSubmit}
                    />
                  </div>
                  {canCreate && (
                    <Button
                      style={styles.buttonAdd}
                      children={strings.actions.addToolDocument}
                      variation="primary"
                      onClick={() => openModal("create")}
                    />
                  )}
                </Flex>
                {toolDocumentsLoading ? (
                  <Loader />
                ) : (
                  <ToolParamatersTable
                    items={columnsMapped}
                    pagination={paginationTokens}
                    onNext={onNext}
                    onPrevious={onPrevious}
                  />
                )}
              </div>
              <ConfirmModal
                visible={modalDetails.visible}
                body={strings.formatString(
                  strings.modal.confirmDeleteQuestion,
                  {
                    name: modalDetails.toolDocumentName,
                  }
                )}
                onCancel={() =>
                  setModalDetails({
                    visible: false,
                    toolDocumentId: null,
                  })
                }
                onConfirm={() =>
                  toolDocumentDelete(modalDetails.toolDocumentId)
                }
              />
            </div>
          </ScrollView>
        </CModalBody>
      </CModal>
      {modalToolDocument.action && (
        <CModal
          size="xl"
          alignment="center"
          visible={modalToolDocument.visible}
          onClose={closeModal}
          backdrop="static"
        >
          <CModalHeader>
            <CModalTitle>
              {modalToolDocument.action === "create" ? (
                <>
                  {strings.modalToolDocument.addTitle} - {toolName}
                </>
              ) : (
                <>
                  {strings.modalToolDocument.updateTitle} - {toolName}
                </>
              )}
            </CModalTitle>
          </CModalHeader>
          <CModalBody style={{ overflow: "hidden" }}>
            <ScrollView maxHeight="75vh">
              {modalToolDocument.action === "create" ? (
                <ToolDocumentCreateForm
                  toolID={toolID}
                  onSuccess={onSuccessAction}
                />
              ) : (
                <ToolDocumentUpdateForm
                  toolDocumentId={modalToolDocument.toolDocumentId}
                  onSuccess={onSuccessAction}
                />
              )}
            </ScrollView>
          </CModalBody>
        </CModal>
      )}
    </>
  );
};

const strings = new LocalizedStrings({
  es: {
    title: "Documentos de Herramienta",
    successDeleted: "Documento de Herramienta eliminado con éxito",
    actions: {
      addToolDocument: "Agregar Documento de Herramienta",
      hide: "Ocultar",
      edit: "Editar",
      delete: "Eliminar",
      download: "Descargar",
    },
    responses: {
      errorFetch: "Error al consultar documento de herramientas",
      errorDownload: "Error en la descarga",
    },
    table: {
      search: {
        label: "Buscar",
        placeholder: "Buscar por palabra clave...",
      },
    },
    modal: {
      confirmDeleteQuestion:
        "¿Estás seguro de eliminar el documento de herramienta {name}?",
    },
    modalToolDocument: {
      addTitle: "Agregar Documento de Herramienta",
      updateTitle: "Actualizar Documento de Herramienta",
    },
    documentReturned: "Heredado del Tipo de Herramienta {name}",
  },
});

const styles = {
  buttonAdd: { height: "fit-content" },
  searchByKeyword: { marginBottom: "15px", width: "25%", minWidth: "300px" },
};

export default ToolDocuments;
